import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from "react";
import { HTTPError } from "ky";
import { authApi } from "../api/auth.api.js";
import { unwrapError } from "../api/client.js";
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [state, setState] = useState({ user: null, loading: true, error: null });
    const refresh = async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const me = await authApi.me();
            setState({ user: me, loading: false, error: null });
        }
        catch (err) {
            // Only an explicit auth rejection (401/403) means "not logged in" and
            // should clear the session. A transient failure (network error, 5xx)
            // must NOT log out a user who already had a valid session — that would
            // turn a blip on /me into an accidental sign-out.
            const status = err instanceof HTTPError ? err.response.status : undefined;
            const isAuthFailure = status === 401 || status === 403;
            if (isAuthFailure) {
                setState({ user: null, loading: false, error: null });
                return;
            }
            // Transient (network / 5xx). Preserve any prior user; surface a
            // retryable error only on the initial load when we have nothing to keep.
            const apiError = await unwrapError(err);
            setState((s) => ({
                ...s,
                loading: false,
                // Keep the existing user (could be null on first load).
                error: s.user ? null : apiError,
            }));
        }
    };
    useEffect(() => {
        void refresh();
        const onUnauth = () => setState({ user: null, loading: false, error: null });
        window.addEventListener("sidecar:unauthenticated", onUnauth);
        return () => window.removeEventListener("sidecar:unauthenticated", onUnauth);
    }, []);
    const signInWithGoogle = async (idToken) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const me = await authApi.signInWithGoogle(idToken);
            setState({ user: me, loading: false, error: null });
        }
        catch (err) {
            const e = await unwrapError(err);
            setState({ user: null, loading: false, error: e });
            throw err;
        }
    };
    const logout = async () => {
        await authApi.logout();
        setState({ user: null, loading: false, error: null });
    };
    const clearError = () => setState((s) => ({ ...s, error: null }));
    return (_jsx(AuthContext.Provider, { value: { ...state, signInWithGoogle, logout, refresh, clearError }, children: children }));
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
